import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { finalize, retry, throwError, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { SKIP_LOADING } from '../interceptors/http-context';

@Injectable({
  providedIn: 'root',
})
export class ConnectionService {
  private global_url = environment.apiUrl;

  // Cache GET read-heavy (profilo + lista eventi): fetch condivisa tra chiamate
  // concorrenti e riusata in sessione, invalidata sulle mutazioni pertinenti.
  private readonly CACHEABLE = ['auth/profile', 'events'];
  // Oltre questa età si rilegge: dati cambiati da un altro dispositivo, o app
  // rimasta aperta a lungo in background sul telefono
  private readonly CACHE_TTL_MS = 5 * 60 * 1000;
  private cache: Record<string, any> = {};
  private cachedAt: Record<string, number> = {};
  private pending: Record<string, { ok: Function; ko: Function }[]> = {};

  constructor(private http: HttpClient) {}

  // Rete assente o server momentaneamente in errore: una GET si può ripetere senza rischi
  private readonly retryTransient = retry({
    count: 1,
    delay: (err: any) =>
      err?.status === 0 || err?.status >= 500 ? timer(800) : throwError(() => err),
  });

  /** Svuota la cache: login, logout, sessione scaduta. */
  reset(): void {
    this.cache = {};
    this.cachedAt = {};
  }

  private invalidate(finalRoute: string): void {
    // Login/registrazione: cambio utente → svuota tutta la cache
    if (finalRoute === 'auth/login' || finalRoute === 'auth/register' || finalRoute === 'auth/google') {
      this.reset();
      return;
    }
    if (finalRoute.startsWith('auth/')) delete this.cache['auth/profile'];
    if (finalRoute.startsWith('events')) delete this.cache['events'];
  }

  /**
   * Estrae la route se contiene un separatore custom '@'
   */
  private extractRoute(route: string): string {
    return route.includes('@') ? route.split('@')[1] : route;
  }

  /**
   * Opzioni HTTP: marca la richiesta con SKIP_LOADING quando la route usa il
   * prefisso '@', così il loadingInterceptor non mostra il loader.
   */
  private opts(route: string): { context: HttpContext } {
    return {
      context: new HttpContext().set(SKIP_LOADING, route.includes('@')),
    };
  }

  getBlob(route: string, callback: Function, callbackError: Function): void {
    const finalRoute = this.extractRoute(route);

    this.http
      .get(`${this.global_url}/${finalRoute}`, {
        ...this.opts(route),
        responseType: 'blob',
      })
      .subscribe({
        next: (blob) => callback(blob),
        error: (error) => callbackError(error),
      });
  }

  postBlob(
    route: string,
    payload: any,
    callback: Function,
    callbackError: Function,
  ): void {
    const finalRoute = this.extractRoute(route);

    this.http
      .post(`${this.global_url}/${finalRoute}`, payload, {
        ...this.opts(route),
        responseType: 'blob',
      })
      .subscribe({
        next: (blob) => callback(blob),
        error: (error) => callbackError(error),
      });
  }

  /**
   * GET request
   */
  get(route: string, callback: Function, callbackError: Function): void {
    const finalRoute = this.extractRoute(route);

    // Cache/dedup per le GET cacheabili
    if (this.CACHEABLE.includes(finalRoute)) {
      const fresh = Date.now() - (this.cachedAt[finalRoute] ?? 0) < this.CACHE_TTL_MS;
      if (finalRoute in this.cache && fresh) {
        callback(this.cache[finalRoute]);
        return;
      }
      if (this.pending[finalRoute]) {
        this.pending[finalRoute].push({ ok: callback, ko: callbackError });
        return;
      }
      this.pending[finalRoute] = [{ ok: callback, ko: callbackError }];

      this.http
        .get(`${this.global_url}/${finalRoute}`, this.opts(route))
        .pipe(this.retryTransient)
        // finalize pulisce sempre la coda — anche se l'errorInterceptor assorbe
        // un 401 (EMPTY) e non scatta né next né error.
        .pipe(finalize(() => delete this.pending[finalRoute]))
        .subscribe({
          next: (result) => {
            this.cache[finalRoute] = result;
            this.cachedAt[finalRoute] = Date.now();
            (this.pending[finalRoute] ?? []).forEach((c) => c.ok(result));
          },
          error: (error) => {
            (this.pending[finalRoute] ?? []).forEach((c) => c.ko(error));
          },
        });
      return;
    }

    this.http
      .get(`${this.global_url}/${finalRoute}`, this.opts(route))
      .pipe(this.retryTransient)
      .subscribe({
        next: (result) => callback(result),
        error: (error) => callbackError(error),
      });
  }

  /**
   * POST request
   */
  post(
    route: string,
    payload: any,
    callback: Function,
    callbackError: Function,
  ): void {
    const finalRoute = this.extractRoute(route);

    this.http
      .post(`${this.global_url}/${finalRoute}`, payload, this.opts(route))
      .subscribe({
        next: (result) => {
          this.invalidate(finalRoute);
          callback(result);
        },
        error: (error) => callbackError(error),
      });
  }

  /**
   * PUT request
   */
  put(
    route: string,
    payload: any,
    callback: Function,
    callbackError: Function,
  ): void {
    const finalRoute = this.extractRoute(route);

    this.http
      .put(`${this.global_url}/${finalRoute}`, payload, this.opts(route))
      .subscribe({
        next: (result) => {
          this.invalidate(finalRoute);
          callback(result);
        },
        error: (error) => callbackError(error),
      });
  }

  /**
   * PATCH request
   */
  patch(
    route: string,
    payload: any,
    callback: Function,
    callbackError: Function,
  ): void {
    const finalRoute = this.extractRoute(route);

    this.http
      .patch(`${this.global_url}/${finalRoute}`, payload, this.opts(route))
      .subscribe({
        next: (result) => {
          this.invalidate(finalRoute);
          callback(result);
        },
        error: (error) => callbackError(error),
      });
  }

  /**
   * DELETE request
   */
  delete(route: string, callback: Function, callbackError: Function): void {
    const finalRoute = this.extractRoute(route);

    this.http
      .delete(`${this.global_url}/${finalRoute}`, this.opts(route))
      .subscribe({
        next: (result) => {
          this.invalidate(finalRoute);
          callback(result);
        },
        error: (error) => callbackError(error),
      });
  }
}
